import type { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import {
  SquarePen,
  Trash2,
  Percent,
  NotebookPen,
  TrendingUp,
  History,
  Calculator,
  DollarSign,
} from 'lucide-react';
import { round } from '@/utils/mathUtils';
import React, { useMemo, memo, useCallback, useState } from 'react';

import { createIDColumn } from '@/components/custom-ui/TableColumns/IDColumn';
import { StyledCellColumn } from '@/components/custom-ui/TableColumns/StyledCell';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverClose,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import type { IPurchaseRequestItemBody } from '@/pages/PurchasesContainer/RequestPerHand/api/HandRequest.interface';
import { calculatePricing } from '@/pages/PurchasesContainer/RequestPerHand/utils/priceCalculations';
import CompetitorsDialog from '@/pages/PurchasesContainer/RequestPerOffer/components/PrepareRequest/CompetitorsDialog';
import DiscountMap from '@/pages/PurchasesContainer/RequestPerOffer/components/PrepareRequest/itemsTable/columns/DiscountCol/DiscountMap';
import PurchaseHistoryDialog from '@/pages/PurchasesContainer/RequestPerOffer/components/PrepareRequest/PurchaseHistoryDialog';
import UnderRequestDialog from '@/pages/PurchasesContainer/RequestPerOffer/components/PrepareRequest/UnderRequestsDialog';
import { useItemStore } from '@/store';
import { DataTable } from '@/UI/TableShadcn';

import StoreStatus from '../StoreStatus';

type RequestItemsProps = {
  requestItems: IPurchaseRequestItemBody[];
  deleteRequestItem: (item: IPurchaseRequestItemBody) => void;
  selectItemForUpdate: (item: IPurchaseRequestItemBody) => void;
  selectedUpdateItem: IPurchaseRequestItemBody | null;
};

// Adapter function to transform IPurchaseRequestItemBody to IOneItemInPrepareForRequest
const adaptItemForDialogs = (
  item: IPurchaseRequestItemBody,
): IOneItemInPrepareForRequest => {
  return {
    itemBodyID: item.TempID?.toString() || '',
    isLastUpdate: false,
    HealthyItemID: item.Item_ID || null,
    itemName: item.ItemName || null,
    healthyItemPrice: item.Price || null,
    ClientItemName: item.ItemName || '',
    Qty: item.Qty || 0,
    TotalAfterDiscount: item.TotalAfterDiscount || 0,
    Price: item.Price || 0,
    Discount: item.Discount || 0,
    DiscountEdit: item.Discount || 0,
    DiscountStatus: item.DiscountStatus || 'static',
    GroupItems: [],
    CompetitorsDiscounts: [],
    WholeSaleDiscount: null,
    WholeSaleStock: 0,
    WholeSaleSales: 0,
    WholeSaleSalesStatus: 'canTransfer',
    WholeSaleDiff: 0,
    RetailDiscount: null,
    RetailStock: 0,
    RetailSales: 0,
    RetailStatus: 'canTransfer',
    RetailDiff: 0,
    HealthyDiscountIN: 0,
    bestDiscount: 0,
    minDiscount: 0,
    underRequests: [],
    totalUnderRequests: 0,
  };
};

// Memoized empty state component to prevent unnecessary re-renders
const EmptyState = memo(() => (
  <div className="flex h-[58vh] items-center justify-center px-4">
    <div className="group relative w-full max-w-xl overflow-hidden rounded-xl border border-border/40 bg-gradient-to-br from-background/80 via-background to-background/90 p-8 shadow-lg transition-all">
      {/* Decorative elements */}
      <div className="absolute -right-6 -top-6 h-28 w-28 rotate-12 rounded-full bg-primary/5 blur-2xl"></div>
      <div className="absolute -bottom-8 -left-8 h-28 w-28 rounded-full bg-blue-500/5 blur-2xl"></div>
      <div className="absolute -bottom-2 right-12 h-20 w-20 rounded-full bg-green-500/5 blur-xl"></div>

      {/* Animated decorative elements */}
      <div className="absolute right-5 top-5 flex flex-col items-end gap-3 opacity-20">
        <div className="flex gap-2">
          <div className="h-2 w-2 rounded-full bg-blue-500"></div>
          <div className="h-2 w-8 rounded-full bg-blue-500/70"></div>
        </div>
        <div className="flex gap-2">
          <div className="h-2 w-2 rounded-full bg-amber-500"></div>
          <div className="h-2 w-10 rounded-full bg-amber-500/70"></div>
        </div>
        <div className="flex gap-2">
          <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
          <div className="h-2 w-6 rounded-full bg-emerald-500/70"></div>
        </div>
      </div>

      {/* Main content container */}
      <div className="relative z-10 flex flex-col items-center gap-6">
        {/* Quranic verse about trust in Allah */}
        <div className="text-center">
          <div className="mb-4 rounded-lg bg-emerald-50 px-6 py-3 dark:bg-emerald-950/30">
            <p className="font-alex text-xl font-bold text-emerald-600 dark:text-emerald-400">
              ﴿ وَمَن يَتَوَكَّلْ عَلَى اللَّهِ فَهُوَ حَسْبُهُ ﴾
            </p>
            <p className="mt-1 text-sm text-emerald-600/80 dark:text-emerald-400/80">
              التوكل على الله
            </p>
          </div>
        </div>

        {/* Text content with gradient headings */}
        <div className="text-center">
          <h3 className="bg-gradient-to-r from-primary via-blue-500 to-primary/80 bg-clip-text pb-1 font-alex text-3xl font-bold text-transparent">
            قائمة الأصناف فارغة
          </h3>

          <p className="mt-3 text-lg font-medium text-foreground/80">
            لم يتم إضافة أي عناصر للطلب بعد
          </p>

          <div className="mt-2 text-sm text-muted-foreground">
            قم بإضافة المنتجات من خلال نافذة البحث أو قائمة المنتجات
          </div>
        </div>

        {/* Keyboard shortcut hint */}
        <div className="mt-2 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-5 py-3 text-sm text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300">
          <div className="flex items-center gap-1">
            <kbd className="rounded border border-blue-300 bg-blue-100 px-1.5 py-0.5 font-sans font-medium text-blue-800 shadow-sm dark:border-blue-800 dark:bg-blue-900 dark:text-blue-200">
              Ctrl
            </kbd>
            <span>+</span>
            <kbd className="rounded border border-blue-300 bg-blue-100 px-1.5 py-0.5 font-sans font-medium text-blue-800 shadow-sm dark:border-blue-800 dark:bg-blue-900 dark:text-blue-200">
              Alt
            </kbd>
            <span>+</span>
            <kbd className="rounded border border-blue-300 bg-blue-100 px-1.5 py-0.5 font-sans font-medium text-blue-800 shadow-sm dark:border-blue-800 dark:bg-blue-900 dark:text-blue-200">
              ↑
            </kbd>
          </div>
          <span className="font-medium">إضافة عنصر جديد</span>
        </div>

        {/* Subtle info banner */}
        <div className="mt-4 rounded-lg bg-primary/5 px-4 py-2 text-center text-sm text-primary/80">
          <span className="font-medium">نصيحة:</span> يمكنك استعراض كافة
          المنتجات المتاحة بالضغط على زر البحث
        </div>

        {/* Important notice about adding a supplier first */}
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-center text-sm font-medium text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-400">
          لابد من اضافة مورد اولا
        </div>
      </div>
    </div>
  </div>
));
EmptyState.displayName = 'EmptyState';

// Memoized cell component to prevent unnecessary re-renders
const ItemNameCell = memo(
  ({
    isSelected,
    itemId,
    itemName,
  }: {
    isSelected: boolean;
    itemId: number;
    itemName: string;
  }) => {
    const { openItemDialog } = useItemStore();

    const handleClick = useCallback(() => {
      openItemDialog(itemId);
    }, [openItemDialog, itemId]);

    return (
      <div
        className={cn(
          'flex h-10 w-full items-center justify-start border border-l-0 border-t-0',
          isSelected && 'bg-blue-50 dark:bg-blue-950',
        )}
      >
        <Button
          variant="link"
          size="default"
          className="line-clamp-1 font-sans text-sm"
          onClick={handleClick}
        >
          {itemName}
        </Button>
      </div>
    );
  },
);
ItemNameCell.displayName = 'ItemNameCell';

// Memoized cell component for standard cells
const StandardCell = memo(
  ({
    isSelected,
    children,
  }: {
    isSelected?: boolean;
    children: React.ReactNode;
  }) => (
    <div
      className={cn(
        'flex h-10 w-full border border-l-0 border-t-0',
        isSelected && 'bg-blue-50 dark:bg-blue-950',
      )}
    >
      {children}
    </div>
  ),
);
StandardCell.displayName = 'StandardCell';

// Memoized component for action buttons
const ActionButtons = memo(
  ({
    item,
    isSelected,
    onDelete,
    onEdit,
  }: {
    item: IPurchaseRequestItemBody;
    isSelected: boolean;
    onDelete: (item: IPurchaseRequestItemBody) => void;
    onEdit: (item: IPurchaseRequestItemBody) => void;
  }) => {
    // Add state for dialog visibility
    const [showCompetitors, setShowCompetitors] = useState(false);
    const [showUnderRequest, setShowUnderRequest] = useState(false);
    const [showIncome, setShowIncome] = useState(false);
    const [showPurchaseHistory, setShowPurchaseHistory] = useState(false);

    // Add handlers for dialog toggling
    const handleShowCompetitors = useCallback(() => {
      setShowCompetitors(true);
    }, []);

    const handleCloseCompetitors = useCallback(() => {
      setShowCompetitors(false);
    }, []);

    const handleShowUnderRequest = useCallback(() => {
      setShowUnderRequest(true);
    }, []);

    const handleCloseUnderRequest = useCallback(() => {
      setShowUnderRequest(false);
    }, []);

    const handleShowIncome = useCallback(() => {
      setShowIncome(true);
    }, []);

    const handleCloseIncome = useCallback(() => {
      setShowIncome(false);
    }, []);

    const handleShowPurchaseHistory = useCallback(() => {
      setShowPurchaseHistory(true);
    }, []);

    const handleClosePurchaseHistory = useCallback(() => {
      setShowPurchaseHistory(false);
    }, []);

    return (
      <>
        <div
          className={cn(
            'flex h-10 w-full items-center justify-center gap-1 border border-l-0 border-t-0 font-beiruti text-sm',
            isSelected && 'bg-blue-50 dark:bg-blue-950',
          )}
        >
          <Button
            className={cn(
              'h-8 w-8',
              isSelected &&
                'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-300',
            )}
            variant="ghost"
            size="icon"
            onClick={() => onEdit(item)}
          >
            <SquarePen />
          </Button>

          {/* Purchase History button */}
          <Button
            variant="ghost"
            className="h-8 w-8 text-blue-500 hover:bg-blue-100 hover:text-blue-600 dark:hover:bg-blue-900"
            size="icon"
            onClick={handleShowPurchaseHistory}
            title="سجل الشراء"
          >
            <History className="h-4 w-4" />
          </Button>

          {/* Income button */}
          <Button
            variant="ghost"
            className="h-8 w-8 text-emerald-500 hover:bg-emerald-100 hover:text-emerald-600 dark:hover:bg-emerald-900"
            size="icon"
            onClick={handleShowIncome}
            title="هامش الربح"
          >
            <TrendingUp className="h-4 w-4" />
          </Button>

          {/* Offers button */}
          <Button
            variant="ghost"
            className="h-8 w-8 text-indigo-500 hover:bg-indigo-100 hover:text-indigo-600 dark:hover:bg-indigo-900"
            size="icon"
            onClick={handleShowCompetitors}
            title="خصومات السوق"
          >
            <Percent className="h-4 w-4" />
          </Button>

          {/* Under Request button */}
          <Button
            variant="ghost"
            className="h-8 w-8 text-red-500 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900"
            size="icon"
            onClick={handleShowUnderRequest}
            title="الطلبات تحت الإنشاء"
          >
            <NotebookPen className="h-4 w-4" />
          </Button>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 text-red-500 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900"
                size="icon"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent side="left" align="center" className="w-auto p-3">
              <div className="flex flex-col items-center gap-2">
                <p className="text-right text-sm font-medium" dir="rtl">
                  هل تريد المسح ؟
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onDelete(item)}
                  >
                    نعم
                  </Button>
                  <PopoverClose asChild>
                    <Button variant="outline" size="sm">
                      لا
                    </Button>
                  </PopoverClose>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Render the dialogs */}
        {showCompetitors && (
          <CompetitorsDialog
            isOpen={showCompetitors}
            closeFn={handleCloseCompetitors}
            item={adaptItemForDialogs(item)}
          />
        )}

        {showUnderRequest && (
          <UnderRequestDialog
            isOpen={showUnderRequest}
            closeFn={handleCloseUnderRequest}
            item={adaptItemForDialogs(item)}
          />
        )}

        {showPurchaseHistory && (
          <PurchaseHistoryDialog
            isOpen={showPurchaseHistory}
            closeFn={handleClosePurchaseHistory}
            item={adaptItemForDialogs(item)}
          />
        )}

        {showIncome && (
          <Dialog open={showIncome} onOpenChange={handleCloseIncome}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader className="mb-2 bg-gradient-to-br from-slate-50 to-white p-3 dark:from-slate-900 dark:to-slate-800/90">
                <DialogTitle className="flex items-center justify-center gap-2 text-center">
                  <DollarSign className="h-5 w-5 text-sky-500 dark:text-sky-400" />
                  <span>تحليل السعر</span>
                </DialogTitle>
                <DialogDescription className="text-center">
                  تفاصيل التسعير للصنف:{' '}
                  <span className="font-medium text-slate-900 dark:text-slate-200">
                    {item.ItemName}
                  </span>
                </DialogDescription>
              </DialogHeader>

              <ProfitAnalysis item={item} />

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={handleCloseIncome}
                  className="border-slate-200 text-slate-800 hover:bg-slate-50 hover:text-slate-900 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-slate-50"
                >
                  إغلاق
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  },
);
ActionButtons.displayName = 'ActionButtons';

// Add the new ProfitAnalysis component
const ProfitAnalysis = memo(({ item }: { item: IPurchaseRequestItemBody }) => {
  // Calculate profit metrics using the pricing utility
  const pricingData = useMemo(() => {
    // Use the price calculation utility for accurate calculations
    const calculationInput = {
      price: item.Price,
      quantity: item.Qty.toString(),
      bonus: item.Bonus?.toString() || '0',
      discount: item.Discount.toString(),
      specialDiscount: item.DiscountPro.toString(),
      tax: item.Tax.toString(),
    };

    return calculatePricing(calculationInput);
  }, [item]);

  return (
    <div className="grid gap-4 py-2">
      {/* Summary section with key metrics */}
      <div className="relative overflow-hidden rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-50/90 p-2 shadow-sm transition-colors duration-300 dark:border-slate-700/60 dark:from-slate-900/90 dark:via-slate-900 dark:to-slate-800/80">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_30%_20%,rgba(224,242,254,0.15),transparent_60%)] dark:bg-[radial-gradient(circle_at_30%_20%,rgba(56,189,248,0.075),transparent_60%)]"></div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <div className="flex flex-col items-center justify-center rounded-md bg-white/80 p-1.5 shadow-sm transition-colors duration-300 dark:bg-slate-800/80">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
              المبلغ قبل الخصم
            </span>
            <span className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
              {round(item.Price * item.Qty, 2).toFixed(2)}
            </span>
          </div>
          <div className="flex flex-col items-center justify-center rounded-md bg-white/80 p-1.5 shadow-sm transition-colors duration-300 dark:bg-slate-800/80">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
              المبلغ بعد الخصم
            </span>
            <span className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
              {round(pricingData.TotalAfterDiscount, 2).toFixed(2)}
            </span>
          </div>
          <div className="flex flex-col items-center justify-center rounded-md bg-white/80 p-1.5 shadow-sm transition-colors duration-300 dark:bg-slate-800/80">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
              نسبة الخصم الصافي
            </span>
            <span className="font-mono text-sm font-bold text-sky-600 dark:text-sky-400">
              {round(pricingData.netDiscount, 2).toFixed(2)}%
            </span>
          </div>
          <div className="flex flex-col items-center justify-center rounded-md bg-white/80 p-1.5 shadow-sm transition-colors duration-300 dark:bg-slate-800/80">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
              السعر الصافي للوحدة
            </span>
            <span className="font-mono text-sm font-bold text-sky-600 dark:text-sky-400">
              {round(pricingData.netPrice, 2).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Price components section */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition-colors duration-300 dark:border-slate-700 dark:bg-slate-900">
        <h4 className="mb-3 flex items-center gap-2 font-medium text-slate-700 dark:text-slate-300">
          <Calculator className="h-4 w-4 text-sky-500" />
          <span>مكونات السعر و الخصم</span>
        </h4>

        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3">
          {/* Price calculations - Column 1 */}
          <div className="space-y-2">
            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-2 transition-colors duration-300 dark:border-slate-700/60 dark:bg-slate-800/60">
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                السعر الأساسي
              </div>
              <div className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {round(pricingData.mainPrice, 3).toFixed(3)}
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-2 transition-colors duration-300 dark:border-slate-700/60 dark:bg-slate-800/60">
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                سعر الوحدة مع الضريبة
              </div>
              <div className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {round(item.Price, 3).toFixed(3)}
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-2 transition-colors duration-300 dark:border-slate-700/60 dark:bg-slate-800/60">
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                خصم الصيدلية
              </div>
              <div className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {round((1 - pricingData.pharmacyDiscount) * 100, 2).toFixed(2)}%
              </div>
            </div>
          </div>

          {/* Price calculations - Column 2 */}
          <div className="space-y-2">
            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-2 transition-colors duration-300 dark:border-slate-700/60 dark:bg-slate-800/60">
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                الخصم الخاص
              </div>
              <div className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {round((1 - pricingData.specialDiscount) * 100, 2).toFixed(2)}%
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-2 transition-colors duration-300 dark:border-slate-700/60 dark:bg-slate-800/60">
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                إجمالي الضريبة
              </div>
              <div className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {round(pricingData.totalTax, 2).toFixed(2)}
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-2 transition-colors duration-300 dark:border-slate-700/60 dark:bg-slate-800/60">
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                السعر الإجمالي
              </div>
              <div className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {round(pricingData.totalPrice, 2).toFixed(2)}
              </div>
            </div>
          </div>

          {/* Price calculations - Column 3 */}
          <div className="space-y-2 md:col-span-1">
            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-2 transition-colors duration-300 dark:border-slate-700/60 dark:bg-slate-800/60">
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                قيمة البونص
              </div>
              <div className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {round(pricingData.bonusValue, 2).toFixed(2)}
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-2 transition-colors duration-300 dark:border-slate-700/60 dark:bg-slate-800/60">
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                متوسط سعر الوحدة
              </div>
              <div className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {round(pricingData.avgPrice, 3).toFixed(3)}
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-2 transition-colors duration-300 dark:border-slate-700/60 dark:bg-slate-800/60">
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                السعر الصافي
              </div>
              <div className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {round(pricingData.netPrice, 3).toFixed(3)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quantity breakdown */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-gradient-to-br from-white to-slate-50/80 p-3 shadow-sm transition-colors duration-300 dark:border-slate-700/60 dark:from-slate-900 dark:to-slate-800/80">
          <h5 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            معلومات الكمية
          </h5>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-md bg-white/90 p-2 shadow-sm transition-colors duration-300 dark:bg-slate-800/80">
              <div className="text-xs text-slate-500 dark:text-slate-400">
                الكمية
              </div>
              <div className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {pricingData.qty}
              </div>
            </div>
            <div className="rounded-md bg-white/90 p-2 shadow-sm transition-colors duration-300 dark:bg-slate-800/80">
              <div className="text-xs text-slate-500 dark:text-slate-400">
                البونص
              </div>
              <div className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {item.Bonus || 0}
              </div>
            </div>
            <div className="rounded-md bg-white/90 p-2 shadow-sm transition-colors duration-300 dark:bg-slate-800/80">
              <div className="text-xs text-slate-500 dark:text-slate-400">
                إجمالي الكمية
              </div>
              <div className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {pricingData.totalQty}
              </div>
            </div>
            <div className="rounded-md bg-white/90 p-2 shadow-sm transition-colors duration-300 dark:bg-slate-800/80">
              <div className="text-xs text-slate-500 dark:text-slate-400">
                كمية الجمهور
              </div>
              <div className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                {item.TotalCustomerPrice
                  ? round(item.TotalCustomerPrice / item.Price, 0)
                  : '-'}
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-gradient-to-br from-white to-slate-50/80 p-3 shadow-sm transition-colors duration-300 dark:border-slate-700/60 dark:from-slate-900 dark:to-slate-800/80">
          <h5 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            معلومات الخصم
          </h5>
          <div className="grid gap-2">
            <div className="flex items-center justify-between rounded-md bg-white/90 p-2 shadow-sm transition-colors duration-300 dark:bg-slate-800/80">
              <span className="text-xs text-slate-600 dark:text-slate-400">
                الخصم على السعر الأساسي:
              </span>
              <span className="font-mono text-sm font-bold text-sky-600 dark:text-sky-400">
                {round(
                  item.Price * (item.Discount / 100) * item.Qty,
                  2,
                ).toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between rounded-md bg-white/90 p-2 shadow-sm transition-colors duration-300 dark:bg-slate-800/80">
              <span className="text-xs text-slate-600 dark:text-slate-400">
                الخصم الصافي على الطلب:
              </span>
              <span className="font-mono text-sm font-bold text-sky-600 dark:text-sky-400">
                {round(pricingData.netDiscount, 2).toFixed(2)}%
              </span>
            </div>
            <div className="flex items-center justify-between rounded-md bg-white/90 p-2 shadow-sm transition-colors duration-300 dark:bg-slate-800/80">
              <span className="text-xs text-slate-600 dark:text-slate-400">
                قيمة التخفيض الصافي:
              </span>
              <span className="font-mono text-sm font-bold text-sky-600 dark:text-sky-400">
                {round(
                  item.Price * item.Qty - pricingData.TotalAfterDiscount,
                  2,
                ).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Memoized BatchAndExpiryCell component
const BatchAndExpiryCell = memo(
  ({
    batchNumber,
    expireDate,
    isSelected,
  }: {
    batchNumber: string | null;
    expireDate: string | null;
    isSelected: boolean;
  }) => {
    const formattedExpireDate = useMemo(() => {
      if (!expireDate) return '-';
      const date = new Date(expireDate);
      return dayjs(date).isValid() ? dayjs(date).format('MM/YYYY') : expireDate;
    }, [expireDate]);

    return (
      <div
        className={cn(
          'flex h-11 w-full flex-col items-center justify-center gap-1 border border-l-0 border-t-0 px-1 py-0.5',
          isSelected && 'bg-blue-50 dark:bg-blue-950',
        )}
      >
        <div className="line-clamp-1 flex w-full items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 px-1 py-1 text-center font-sans text-sm font-semibold tracking-wider text-emerald-800 shadow-sm dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
          <span className="mx-auto mt-1 text-center">{batchNumber || '-'}</span>
        </div>
        <div className="line-clamp-1 flex w-full items-center justify-center rounded-md border border-amber-200 bg-amber-50 px-1 py-1 text-center font-sans text-sm font-semibold tracking-wider text-amber-800 shadow-sm dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
          <span className="mx-auto mt-1 text-center">
            {formattedExpireDate}
          </span>
        </div>
      </div>
    );
  },
);
BatchAndExpiryCell.displayName = 'BatchAndExpiryCell';

// Main component - memoized for performance
const RequestItems = memo(
  ({
    requestItems,
    deleteRequestItem,
    selectItemForUpdate,
    selectedUpdateItem,
  }: RequestItemsProps) => {
    // Memoized columns definition
    const columns = useMemo<ColumnDef<IPurchaseRequestItemBody>[]>(
      () => [
        createIDColumn<IPurchaseRequestItemBody>(),
        {
          accessorKey: 'ItemName',
          header: 'الصنف',
          cell: (row) => (
            <ItemNameCell
              isSelected={
                selectedUpdateItem?.TempID === row.row.original.TempID
              }
              itemId={row.row.original.Item_ID}
              itemName={row.row.original.ItemName}
            />
          ),
        },
        {
          accessorKey: 'Qty',
          header: 'كمية',
          cell: (row) => {
            const isSelected =
              selectedUpdateItem?.TempID === row.row.original.TempID;
            return (
              <StandardCell isSelected={isSelected}>
                <StyledCellColumn
                  className="m-0 h-full w-full"
                  value={row.row.original.Qty}
                />
              </StandardCell>
            );
          },
        },
        {
          accessorKey: 'Price',
          header: 'جمهور',
          cell: (row) => (
            <StandardCell>
              <StyledCellColumn
                className="m-0 h-full w-full"
                color="no-color"
                value={row.row.original.Price}
                comma={2}
              />
            </StandardCell>
          ),
        },
        {
          accessorKey: 'TotalCustomerPrice',
          header: 'ج.جمهور',
          cell: (row) => (
            <StandardCell>
              <StyledCellColumn
                className="m-0 h-full w-full"
                color="no-color"
                value={row.row.original.TotalCustomerPrice}
                comma={2}
              />
            </StandardCell>
          ),
        },
        {
          accessorKey: 'Tax',
          header: 'ض',
          cell: (row) => (
            <StandardCell>
              <StyledCellColumn
                className="m-0 h-full w-full"
                color="no-color"
                value={row.row.original.Tax}
                comma={2}
              />
            </StandardCell>
          ),
        },
        {
          accessorKey: 'MainPrice',
          header: 'أساسي',
          cell: (row) => (
            <StandardCell>
              <StyledCellColumn
                className="m-0 h-full w-full"
                color="no-color"
                value={row.row.original.MainPrice}
                comma={2}
              />
            </StandardCell>
          ),
        },
        //Discount
        {
          accessorKey: 'Discount',
          header: 'خصم',
          cell: (row) => {
            const discountStatus = row.row.original.DiscountStatus;

            return (
              <div className="flex h-10 w-full items-center justify-center border border-l-0 border-t-0">
                <StyledCellColumn
                  className="m-0 h-full w-full rounded-none border-l p-0"
                  color={
                    discountStatus === 'best'
                      ? 'green'
                      : discountStatus === 'accept'
                        ? 'gray'
                        : discountStatus === 'bad'
                          ? 'red'
                          : discountStatus === 'only'
                            ? 'indigo'
                            : 'fuchsia'
                  }
                  comma={3}
                  value={row.row.original.Discount}
                />
                <DiscountMap discountStatus={discountStatus} />
              </div>
            );
          },
        },
        //Special Discount
        {
          accessorKey: 'DiscountPro',
          header: 'ت.دفع',
          cell: (row) => (
            <StandardCell>
              <StyledCellColumn
                className="m-0 h-full w-full"
                color="no-color"
                value={row.row.original.DiscountPro}
                comma={2}
              />
            </StandardCell>
          ),
        },
        {
          accessorKey: 'TotalAfterDiscount',
          header: 'ج.بعد',
          cell: (row) => (
            <StandardCell>
              <StyledCellColumn
                className="m-0 h-full w-full"
                color="red"
                value={row.row.original.TotalAfterDiscount}
                comma={2}
              />
            </StandardCell>
          ),
        },
        {
          accessorKey: 'Bonus',
          header: 'بونص',
          cell: (row) => (
            <StandardCell>
              <StyledCellColumn
                className="m-0 h-full w-full"
                color="green"
                value={row.row.original.Bonus}
                comma={2}
              />
            </StandardCell>
          ),
        },
        {
          accessorKey: 'TotalTax',
          header: 'ج.ض',
          cell: (row) => (
            <StandardCell>
              <StyledCellColumn
                className="m-0 h-full w-full"
                color="no-color"
                value={row.row.original.TotalTax}
                comma={2}
              />
            </StandardCell>
          ),
        },
        {
          accessorKey: 'BNO',
          header: 'تشغيلة',
          cell: (row) => (
            <BatchAndExpiryCell
              batchNumber={row.row.original.BNO}
              expireDate={row.row.original.ExpireDate}
              isSelected={
                selectedUpdateItem?.TempID === row.row.original.TempID
              }
            />
          ),
        },
        {
          accessorKey: 'RetailStatus',
          header: 'حالة',
          cell: (row) => {
            const retailStatus = row.row.original.RetailStatus;
            const WholeSaleStatus = row.row.original.WholeSaleStatus;
            return (
              <div className="flex h-10 w-full items-center justify-center gap-0.5 border border-l-0 border-t-0">
                <StoreStatus status={retailStatus} store="مخزن القطاعي" />
                <StoreStatus status={WholeSaleStatus} store="مخزن الجملة" />
              </div>
            );
          },
        },
        {
          accessorKey: 'TempID',
          header: '-',
          cell: ({ row }) => (
            <ActionButtons
              item={row.original}
              isSelected={selectedUpdateItem?.TempID === row.original.TempID}
              onDelete={deleteRequestItem}
              onEdit={selectItemForUpdate}
            />
          ),
        },
      ],
      [deleteRequestItem, selectItemForUpdate, selectedUpdateItem],
    );

    // Prevent unnecessary re-renders when requestItems.length === 0
    const hasItems = requestItems.length > 0;

    return (
      <div className="p-2">
        {hasItems ? (
          <DataTable
            columns={columns}
            data={requestItems}
            isTopToolbar={true}
            isPaginated={false}
            heightTable="53vh"
            visibleColumns={[
              'TotalCustomerPrice',
              'TotalTax',
              'Tax',
              'MainPrice',
            ]}
          />
        ) : (
          <EmptyState />
        )}
      </div>
    );
  },
);

RequestItems.displayName = 'RequestItems';
export default RequestItems;
